import React, { useEffect, useMemo, useState } from "react";
import Select from "react-select";
import { useNavigate } from "react-router-dom";
import { ReactComponent as CrosIcon } from "../../../assets/crossIcon.svg";
import { InterUserMangementServices } from "../../../services/InternalUserMangementServices";
import { RpaServices } from "../../../services/RpaServices";
import { handleApiErrorResponses } from "../../../utils/ApisErrorHandling";
import { CaseServices } from "../../../services/CaseServices";
import downIcon from "../../../assets/query-down-icon.png";
import { toast } from "react-toastify";

export default function QueryForwardDoctorModal({
  open,
  onClose,
  queryId,
  mappedDocuments,
  caseId,
  fetchQueries,
  fetchCaseQueryCounts,
}) {
  const [doctorOptions, setDoctorOptions] = useState([]);
  const [selectedDoctors, setSelectedDoctors] = useState([]);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();

  const selectStyles = useMemo(
    () => ({
      control: (base) => ({
        ...base,
        borderRadius: 44,
        minHeight: 50,
        borderColor: "#EDEDED",
        boxShadow: "none",
        fontSize: "14px",
        fontWeight: 500,
        fontFamily: "Lufga",
      }),
      placeholder: (base) => ({
        ...base,
        color: "rgba(25, 25, 25, 1)",
        fontSize: "14px",
        fontWeight: 500,
      }),
      singleValue: (base) => ({
        ...base,
        fontSize: "14px",
        fontWeight: 500,
      }),
      option: (base) => ({
        ...base,
        fontSize: "14px",
        fontWeight: 500,
      }),
      indicatorSeparator: () => ({ display: "none" }),
    }),
    [],
  );

  useEffect(() => {
    if (!open) return;
    setSelectedDoctors([]);
    setComment("");

    (async () => {
      try {
        const res = await InterUserMangementServices.getCcDoctorTeamUsers({
          limit: 100,
          page: 1,
          searchKey: "",
        });
        const data = res?.data ?? {};
        const teamUsers = Array.isArray(data.teamUsers) ? data.teamUsers : [];
        const opts = teamUsers
          .filter((u) => u?.id)
          .map((u) => {
            const label =
              String(u.fullName ?? "").trim() ||
              String(u.userName ?? "").trim() ||
              String(u.email ?? "").trim() ||
              "—";
            return { value: String(u.id), label };
          });
        setDoctorOptions(opts);
      } catch {
        setDoctorOptions([]);
      }
    })();
  }, [open]);

  async function onSubmit() {
    if (!queryId || isSubmitting) return;
    setIsSubmitting(true);

    try {
      const ids = Array.isArray(selectedDoctors)
        ? selectedDoctors.map((x) => x?.value).filter(Boolean)
        : [];

      if (mappedDocuments?.length > 0) {
        await CaseServices.updateMappedDocuments(mappedDocuments, caseId);
      }

      const response = await RpaServices.forwardCaseQuery({
        queryId,
        forwardedToTeam: "ccdoctor",
        forwardedToIds: ids,
        comment,
      });

      // await RpaServices.removeWorkInProgress(queryId);

      const message = response?.data?.message || "Query forwarded successfully";
      toast.success(message);

      // ✅ ADD THIS
      if (fetchQueries) {
        await fetchQueries();
      }
      if (fetchCaseQueryCounts) {
        await fetchCaseQueryCounts();
      }
      onClose();
      navigate("/dashboard/ActiveCases");
      // close AFTER refresh
    } catch (error) {
      handleApiErrorResponses(error);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[1300] flex items-center justify-center bg-[#017C5780]"
      role="dialog"
      aria-modal="true"
    >
      <div className="relative w-[1200px] max-w-[92vw]  max-h-[92vh] rounded-[32px] bg-white shadow-lg p-6">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-6 top-5 text-[#191919] text-[33px] leading-none bg-transparent border-none outline-none"
          aria-label="Close"
        >
          <CrosIcon />
        </button>

        <div className="">
          <div className="font-[Lufga] font-medium text-[20px] xl:text-[24px] 2xl:text-[24px] leading-[100%] tracking-[0em] text-[#191919]">
            Query Forward to Doctor
          </div>
        </div>

        <div className="w-full h-[1px] bg-[#E8E8E8] my-6" />

        <div className=" flex flex-col gap-6">
          <div>
            <div className="relative w-full">
              <Select
                classNamePrefix="qfd"
                placeholder="Forward to All Doctors"
                isMulti
                closeMenuOnSelect={false}
                value={selectedDoctors}
                onChange={(val) => setSelectedDoctors(val || [])}
                options={doctorOptions}
                styles={{
                  ...selectStyles,
                  control: (base) => ({
                    ...base,
                    width: "100%",
                    minHeight: window.innerWidth >= 1280 ? 68 : 52,
                    height: window.innerWidth >= 1280 ? 68 : 52,
                    borderRadius: 100,
                    border: "2px solid #EDEDED",
                    boxShadow: "none",
                    paddingLeft: "24px",
                    paddingRight: "50px",
                    fontSize: window.innerWidth >= 1280 ? "14px" : "12px",
                    fontWeight: 500,
                    fontFamily: "Lufga",
                  }),
                  dropdownIndicator: () => ({
                    display: "none",
                  }),
                  indicatorSeparator: () => ({
                    display: "none",
                  }),
                  placeholder: (base) => ({
                    ...base,
                    color: "#191919",
                    fontSize: window.innerWidth >= 1280 ? "14px" : "12px",
                    fontWeight: 500,
                    fontFamily: "Lufga",
                  }),
                }}
              />

              <img
                src={downIcon}
                alt="dropdown-icon"
                className="absolute right-6 top-1/2 -translate-y-1/2 pointer-events-none w-[18px] h-[10px] xl:w-[22px] xl:h-[12px] 2xl:w-[22px] 2xl:h-[12px]"
              />
            </div>
          </div>

          <div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Comment ( Multiline )"
              className="w-full resize-none rounded-[50px] border-2 border-[#EDEDED] px-6 py-[30px] font-[Lufga] font-medium text-[14px] leading-[20px] tracking-[-0.4px] text-[#797979] placeholder:text-[#797979] outline-none"
              rows={4}
            />
          </div>

          <div className="flex justify-center">
            <button
              type="button"
              onClick={onSubmit}
              disabled={
                !queryId ||
                isSubmitting ||
                selectedDoctors?.length === 0 ||
                !comment
              }
              className={`w-[240px] h-[70px] flex items-center justify-center gap-[10px] rounded-full border border-[#017C57] bg-[#017C57] py-[24px] px-[20px] text-[16px] font-medium text-white hover:bg-[#016a5f] transition ${selectedDoctors?.length === 0 || !comment ? "cursor-not-allowed" : ""}`}
            >
              {isSubmitting ? "Forwarding..." : "Forward to Doctor"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
